import { getObjectionNextStep } from 'domains/ReportBuilder/model/actions';
import { useNextStepData } from 'domains/ReportBuilder/model/selectors';
import {
  TGroupingItem,
  TNextStepData,
  TObjectionTypes,
} from 'domains/ReportBuilder/types';
import { useState, useCallback, useMemo, useEffect, useRef, RefObject, createRef } from 'react';
import { useDispatchApp } from 'redux/rootSelectors';
import { changeInternalIdToXxhash } from 'domains/ReportBuilder/utils';
import i18n from 'i18n';
import { setClearNextStep } from 'domains/ReportBuilder/reducer';
import { TUseReportFilters } from '../types';

const STEPS: TObjectionTypes[] = [
  'agency',
  'client',
  'campaign',
  'creative',
] as const;

export const useReportFilters: TUseReportFilters = (
  formik,
  objectionBy,
  objections,
  filterings,
  setObjections,
  stateOfPanel,
  currentUserStep,
  containerRef,
) => {
  const dispatch = useDispatchApp();
  const { data, isLoading: isNextStepLoading } = useNextStepData();

  const [currentStepKey, setCurrentStepKey] =
    useState<TObjectionTypes>(currentUserStep);

  const [showStepBtn, setShowStepBtn] = useState<
    Record<TObjectionTypes, boolean>
  >({
    agency: false,
    client: false,
    campaign: false,
    creative: false,
  });

  const getNextStep = (
    currentStep: TObjectionTypes,
  ): TObjectionTypes | null => {
    const currentIndex = STEPS.indexOf(currentStep);

    if (currentIndex === -1 || currentIndex === STEPS.length - 1) {
      return null;
    }

    return STEPS[currentIndex + 1];
  };

  const handleSubmit = useCallback(() => {
    const currentValue = objectionBy[currentStepKey];
    const currentObjData = objections[currentStepKey].data as TNextStepData[];

    const xxhashParams = changeInternalIdToXxhash(currentValue, currentObjData);

    if (!xxhashParams) {
      setShowStepBtn((prev) => ({ ...prev, [currentStepKey]: false, }));
      return;
    }

    const xxhashList = xxhashParams.split(',').filter(Boolean);

    if (xxhashList.length === 0) {
      return;
    }

    if (formik.values.period) {
      dispatch(
        getObjectionNextStep({
          type: currentStepKey,
          xxhash_list: xxhashList,
          period: formik.values.period,
        }),
      );
      setShowStepBtn((prev) => ({ ...prev, [currentStepKey]: false }));
    }

    const nextStep = getNextStep(currentStepKey);
    if (nextStep) {
      setCurrentStepKey(nextStep);
    }
  }, [currentStepKey, objectionBy, formik.values.period, objections]);



  // Выбрано ли агентство
  // const hasAgency = Boolean(
  //   formik.values.reportFilters.filterBy.agency?.length,
  // );

  const noTypeLastSort = Object.entries(filterings).sort(([, a], [, b]) => {
    if (a.type === 'no') return 1;
    if (b.type === 'no') return -1;

    return 0;
  });

  const selectRefs = useRef<Record<string, RefObject<any>>>({});

  /**
 * Возвращает ref для конкретного ReportSelect.
 * Если ref ещё не существует — создаём его.
 */
  const getSelectRef = (key: string) => {
    if (!selectRefs.current[key]) {
      selectRefs.current[key] = createRef();
    }

    return selectRefs.current[key];
  };

  /** Закрывает все открытые ReportSelect. */
  const closeAllSelects = () => {
    Object.values(selectRefs.current).forEach((selectRef) => {
      selectRef.current?.onMenuClose();
    });
  };

  useEffect(() => {
    if (!stateOfPanel) {
      closeAllSelects();
    }
  }, [stateOfPanel]);

  /** Закрываем все ReportSelect при скролле контейнера. */
  useEffect(() => {
    if (!containerRef?.current) {
      return undefined;
    }

    const container = containerRef.current;

    const handleScroll = () => {
      closeAllSelects();
    };

    container.addEventListener('scroll', handleScroll, {
      passive: true,
    });

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [containerRef]);

  const getCurrentHint = useMemo((): string => {
    const hints: Partial<Record<TObjectionTypes, string>> = {
      agency: i18n.t(`reportbuilder.agencyHint`),
      client: i18n.t(`reportbuilder.clientHint`),
      campaign: i18n.t(`reportbuilder.campaignHint`),
      creative: '',
    };
    return hints[currentStepKey] ?? '';
  }, [currentStepKey, stateOfPanel]);

  // Получаем последний ключ из objections
  const lastObjectionKey = useMemo(() => {
    const keys = Object.keys(objections) as TObjectionTypes[];
    return keys.length > 0 ? keys[keys.length - 1] : null;
  }, [objections]);

  /** Мне кажется эта часть ничего не делает... Я удаляю? Да? Так как есть управление кнопками подтвержения ниже  */
  // useEffect(() => {
  //   // Если агентства больше нет — возвращаем компонент в исходное состояние
  //   if (!hasAgency) {
  //     setShowFilters(false);
  //   }
  // }, [hasAgency]);

  useEffect(() => {
    if (formik.values.period) {
      dispatch(setClearNextStep());
      if (objections && Object.entries(objections).length > 0) {
        const startObjectionsElement = Object.fromEntries([
          Object.entries(objections).filter((obj) => Boolean(obj[1]))[0],
        ]) as React.SetStateAction<Record<TObjectionTypes, TGroupingItem>>;

        setObjections(startObjectionsElement);
      }

      setShowStepBtn({
        agency: false,
        client: false,
        campaign: false,
        creative: true,
      });

      formik.setFieldValue('reportFilters.objectionBy.agency', '');
      formik.setFieldValue('reportFilters.objectionBy.client', '');
      formik.setFieldValue('reportFilters.objectionBy.campaign', '');
      formik.setFieldValue('reportFilters.objectionBy.creative', '');
    }
    setCurrentStepKey(currentUserStep);
  }, [formik.values.period]);

  useEffect(() => {
    const { agency } = formik.values.reportFilters.objectionBy;
    if (agency) {
      formik.setFieldValue('reportFilters.objectionBy.client', '');
      formik.setFieldValue('reportFilters.objectionBy.campaign', '');
      formik.setFieldValue('reportFilters.objectionBy.creative', '');

      setObjections(
        (prev) =>
        ({
          agency: prev.agency,
        } as Record<TObjectionTypes, TGroupingItem>),
      );

      setCurrentStepKey('agency');
      setShowStepBtn({
        agency: true,
        client: false,
        campaign: false,
        creative: false,
      });
    }
  }, [formik.values.reportFilters.objectionBy.agency]);

  useEffect(() => {
    const { client } = formik.values.reportFilters.objectionBy;
    if (client) {
      formik.setFieldValue('reportFilters.objectionBy.campaign', '');
      formik.setFieldValue('reportFilters.objectionBy.creative', '');

      setObjections(
        (prev) =>
        ({
          agency: prev.agency,
          client: prev.client,
        } as Record<TObjectionTypes, TGroupingItem>),
      );
      setCurrentStepKey('client');
      setShowStepBtn({
        agency: false,
        client: true,
        campaign: false,
        creative: false,
      });
    }
  }, [formik.values.reportFilters.objectionBy.client]);

  useEffect(() => {
    const { campaign } = formik.values.reportFilters.objectionBy;
    if (campaign) {
      formik.setFieldValue('reportFilters.objectionBy.creative', '');

      setObjections(
        (prev) =>
        ({
          agency: prev.agency,
          client: prev.client,
          campaign: prev.campaign,
        } as Record<TObjectionTypes, TGroupingItem>),
      );
      setCurrentStepKey('campaign');
      setShowStepBtn({
        agency: false,
        client: false,
        campaign: true,
        creative: false,
      });
    }
  }, [formik.values.reportFilters.objectionBy.campaign]);

  useEffect(() => {
    const { creative } = formik.values.reportFilters.objectionBy;
    if (creative) {
      setCurrentStepKey('creative');
      setShowStepBtn({
        agency: false,
        client: false,
        campaign: false,
        creative: true,
      });
    }
  }, [formik.values.reportFilters.objectionBy.creative]);

  useEffect(() => {
    if (!data) {
      setCurrentStepKey(currentUserStep);
    }
  }, [data, currentUserStep]);

  useEffect(() => {
    if (!Object.keys(formik.values.reportFilters.objectionBy).length) {
      setShowStepBtn({
        agency: false,
        client: false,
        campaign: false,
        creative: true,
      });
    }
  }, [formik.values.reportFilters.objectionBy]);

  return {
    isNextStepLoading,
    currentStepKey,
    showStepBtn,
    noTypeLastSort,
    getCurrentHint,
    lastObjectionKey,
    currentUserStep,
    handleSubmit,
    closeAllSelects,
    getSelectRef,
  };
};
